import { ReactNode } from "react";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BranchReadiness } from "../../types/branch.type";
import BranchItemTable from "./BranchItemTable";
import { resolveReadinessStatus } from "./branchStatus";
import { GENERAL_TASKS, READINESS_GUIDE, ROUTE_ITEM_COLUMNS } from "./constants";
import ReadinessChip from "./ReadinessChip";
import ReadinessGuideList from "./ReadinessGuide";
import RouteReadinessTable from "./RouteReadinessTable";

const ROUTE_ITEM_GUIDES = ROUTE_ITEM_COLUMNS.map(({ key }) => READINESS_GUIDE[key]);

interface ChecklistSectionProps {
  title: string;
  caption: string;
  children: ReactNode;
}

function ChecklistSection({ title, caption, children }: ChecklistSectionProps) {
  return (
    <Stack gap={1}>
      <Stack>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Stack>
      {children}
    </Stack>
  );
}

interface ReadinessChecklistProps {
  readiness: BranchReadiness | null;
  loadFailed: boolean;
  canOpenRoutes: boolean;
  onReadinessChanged: () => void;
}

/** What the branch still lacks before its routes can open, who does it, and where. */
export default function ReadinessChecklist({
  readiness,
  loadFailed,
  canOpenRoutes,
  onReadinessChanged,
}: ReadinessChecklistProps) {
  if (loadFailed) {
    return (
      <Typography color="text.secondary">โหลดเช็คลิสต์ความพร้อมไม่สำเร็จ</Typography>
    );
  }

  if (!readiness) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  if (readiness.is_head_office) {
    return (
      <Alert severity="info">
        สำนักงานใหญ่ไม่ได้ขายสินค้า จึงไม่มีเช็คลิสต์ความพร้อมเปิดใช้สาย
      </Alert>
    );
  }

  const { summary, routes } = readiness;
  const hasUnknownItems = summary.required_unknown_count > 0;

  return (
    <Stack gap={3}>
      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
        <ReadinessChip view={resolveReadinessStatus(summary, false)} />
        <Typography variant="body2" color="text.secondary">
          สายที่ผ่านเงื่อนไขเปิดใช้ {summary.ready_route_count} จาก {routes.length} สาย
        </Typography>
      </Stack>

      {hasUnknownItems && (
        <Alert severity="warning">
          ระบบอ่านข้อมูลผู้ใช้ไม่ได้ รายการผู้ใช้จึงแสดงเป็น "ตรวจสอบไม่ได้" กรุณาโหลดหน้านี้ใหม่ภายหลัง
        </Alert>
      )}

      <ChecklistSection
        title="ระดับสาขา"
        caption="บังคับ = ต้องครบก่อนเปิดใช้สายใดก็ได้ · แนะนำ = ไม่บล็อกการเปิดใช้"
      >
        <BranchItemTable items={readiness.branch_items} />
      </ChecklistSection>

      <ChecklistSection
        title="รายสาย"
        caption="กดเปิดใช้ได้เมื่อรายการบังคับของสาขาและของสายนั้นครบ"
      >
        <RouteReadinessTable
          routes={routes}
          canOpenRoutes={canOpenRoutes}
          onReadinessChanged={onReadinessChanged}
        />
        <ReadinessGuideList guides={ROUTE_ITEM_GUIDES} />
      </ChecklistSection>

      <ChecklistSection
        title="งานอื่นของสาขา"
        caption="ไม่อยู่ในเงื่อนไขเปิดใช้สาย แต่สาขาต้องมีก่อนเริ่มวิ่งจริง"
      >
        <ReadinessGuideList guides={GENERAL_TASKS} />
      </ChecklistSection>
    </Stack>
  );
}
